import type {
  AdminReportBuildRequest,
  AdminReportBuildResponse,
  AdminReportDownloadResponse,
  RelatedEntityTypeEnum,
  ReportTypeEnum,
  ReportsRead,
  TransactionReportByProductsParams,
  TransactionReportParams,
} from '@/types/reports';
import api, { API_PREFIX } from './api';

export const buildAdminStoredReport = async (
  reportType: ReportTypeEnum,
  body: AdminReportBuildRequest,
  orgId?: string
): Promise<AdminReportBuildResponse> => {
  const response = await api.post<AdminReportBuildResponse>(
    `${API_PREFIX}/reports/${encodeURIComponent(reportType)}/build`,
    {
      related_entity_id: body.related_entity_id,
      related_entity_type: body.related_entity_type,
      queue_payload: body.queue_payload ?? {},
    },
    orgId ? { headers: { 'x-organization-id': orgId } } : undefined
  );
  return response.data;
};

export const getAdminStoredReport = async (
  reportType: ReportTypeEnum,
  relatedEntityType: RelatedEntityTypeEnum,
  relatedEntityId: string
): Promise<ReportsRead> => {
  const response = await api.get<ReportsRead>(
    `${API_PREFIX}/reports/${encodeURIComponent(reportType)}/${encodeURIComponent(relatedEntityType)}/${encodeURIComponent(relatedEntityId)}`
  );
  return response.data;
};

export const downloadAdminStoredReport = async (
  reportType: ReportTypeEnum,
  relatedEntityType: RelatedEntityTypeEnum,
  relatedEntityId: string
): Promise<AdminReportDownloadResponse> => {
  const response = await api.get<AdminReportDownloadResponse>(
    `${API_PREFIX}/reports/${encodeURIComponent(reportType)}/download`,
    {
      params: {
        related_entity_id: relatedEntityId,
        related_entity_type: relatedEntityType,
      },
    }
  );
  return response.data;
};

export const downloadStableSetupReport = async (): Promise<Blob> => {
  const response = await api.get(`${API_PREFIX}/reports/stable-setup`, {
    responseType: 'blob',
  });
  return response.data;
};

export const downloadHealthCheckReport = async (): Promise<Blob> => {
  const response = await api.get(`${API_PREFIX}/reports/health-check`, {
    responseType: 'blob',
  });
  return response.data;
};

export const generateTaxuallyReport = async (
  orgId: string
): Promise<{ status: number; data: Blob; headers: { 'content-disposition'?: string } }> => {
  const response = await api.get(`${API_PREFIX}/reports/taxually`, {
    headers: { 'x-organization-id': orgId },
    responseType: 'blob',
  });
  return {
    status: response.status,
    data: response.data,
    headers: response.headers as { 'content-disposition'?: string },
  };
};

export type RemittanceReportParams = {
  start_date?: string;
  end_date?: string;
  country_code?: string;
  state_code?: string;
};

export const downloadRemittanceReport = async (
  orgId: string,
  params: RemittanceReportParams
): Promise<{ status: number; data: Blob; headers: { 'content-disposition'?: string } }> => {
  const response = await api.get(`${API_PREFIX}/filings/remittance-report`, {
    params,
    headers: { 'x-organization-id': orgId },
    responseType: 'blob',
  });
  return {
    status: response.status,
    data: response.data,
    headers: response.headers as { 'content-disposition'?: string },
  };
};

export const downloadTransactionReport = async (
  orgId: string,
  reportType: string,
  params: TransactionReportParams
): Promise<{ status: number; data: Blob; headers: { 'content-disposition'?: string } }> => {
  const { connection_ids, country_codes, ...rest } = params;
  const queryParams: Record<string, string | string[]> = {
    ...rest,
    report_type: reportType,
  };
  if (connection_ids?.length) {
    queryParams.connection_ids = connection_ids;
  }
  if (country_codes?.length) {
    queryParams.country_codes = country_codes;
  }

  const response = await api.get(`${API_PREFIX}/transactions/reports/details`, {
    params: queryParams,
    paramsSerializer: {
      indexes: null,
    },
    headers: {
      'x-organization-id': orgId,
    },
    responseType: 'blob',
  });
  return {
    status: response.status,
    data: response.data,
    headers: response.headers as { 'content-disposition'?: string },
  };
};

export const downloadTransactionItemsReport = async (
  orgId: string,
  params: TransactionReportByProductsParams
): Promise<{ status: number; data: Blob; headers: { 'content-disposition'?: string } }> => {
  const { connection_ids, product_ids, ...rest } = params;
  const queryParams: Record<string, string | string[]> = { ...rest };
  if (connection_ids?.length) {
    queryParams.connection_ids = connection_ids;
  }
  if (product_ids?.length) {
    queryParams.product_ids = product_ids;
  }

  const response = await api.get(`${API_PREFIX}/transactions/reports/items`, {
    params: queryParams,
    paramsSerializer: {
      indexes: null,
    },
    headers: {
      'x-organization-id': orgId,
    },
    responseType: 'blob',
  });
  return {
    status: response.status,
    data: response.data,
    headers: response.headers as { 'content-disposition'?: string },
  };
};
